import DetailPair from "../UI/DetailPair";
import Heading from "../UI/Heading";
import LinkPair from "../UI/LinkPair";
import SelectPair from "../UI/SelectPair";
import { TbChevronDown, TbChevronUp, TbFileTypePdf } from "react-icons/tb";
import { Fragment, useEffect, useState } from "react";
import { requestAdminAPI } from "../../api/posh-api";
import LoadingSpinner from "../UI/LoadingSpinner";
import { MdCheck, MdClose, MdEdit } from "react-icons/md";
import uploadFile from "../../util/upload-file";

export default function CreditApplication({ partner = {}, refresh }) {
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [edit, setEdit] = useState(false);
  const [uploading, setUploading] = useState(false);

  // ======================== GET PDF ========================
  const getPdf = async () => {
    try {
      setLoading(true);
      const response = await requestAdminAPI("get-credit-app-pdf", {
        partnerId: partner.id,
      });
      console.log(response);
      window.open(response, "_blank");
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // ======================== EDIT CREDIT APP ========================
  const editCreditApp = async () => {
    try {
      setLoading(true);
      const payload = {
        partnerId: partner.id,
        driversLicenseNumber: document.getElementById("driversLicenseNumber")
          .value,
        ssn: document.getElementById("ssn").value,
        address: document.getElementById("address").value,
        residenceType: document.getElementById("residenceType").value,
        monthlyRent: parseFloat(document.getElementById("monthlyRent").value),
        yearsAtResidence: parseInt(
          document.getElementById("yearsAtResidence").value
        ),
        monthsAtResidence: parseInt(
          document.getElementById("monthsAtResidence").value
        ),
        employer: document.getElementById("employer").value,
        occupation: document.getElementById("occupation").value,
        employmentYears: parseInt(
          document.getElementById("employmentYears").value
        ),
        monthlySalary: parseFloat(
          document.getElementById("monthlySalary").value
        ),
        otherIncome: parseFloat(document.getElementById("otherIncome").value),
      };
      const files = {
        proofOfResidence: document.getElementById("proofOfResidence")?.files,
        payStubs: document.getElementById("payStubs")?.files,
        bankStatements: document.getElementById("bankStatements")?.files,
        taxReturns: document.getElementById("taxReturns")?.files,
        ssnFront: document.getElementById("ssnFront")?.files[0],
        selfieId: document.getElementById("selfieId")?.files[0],
        creditAppAgreement:
          document.getElementById("creditAppAgreement")?.files[0],
      };

      // Upload Files
      for (const key in files) {
        if (!files[key] || files[key]?.length === 0) continue;
        if (files[key] instanceof FileList) {
          const urls = [];
          for (let i = 0; i < files[key].length; i++) {
            const url = await uploadFile(
              `partners/${
                partner.id
              }/credit-app/${key}-${new Date().getTime()}`,
              files[key][i],
              setUploading
            );
            urls.push(url);
          }
          payload[key] = urls;
        } else {
          const url = await uploadFile(
            `partners/${partner.id}/credit-app/${key}-${new Date().getTime()}`,
            files[key],
            setUploading
          );
          payload[key] = url;
        }
      }
      await requestAdminAPI("edit-credit-app", payload);
      refresh && refresh();
      setEdit(false);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (edit) setShow(true);
  }, [edit]);

  return (
    <div className="grid">
      <Heading
        title="Credit Application"
        onClick={() => setShow(!show)}
        isBackArrow={false}
      >
        {edit ? (
          <Fragment>
            <MdCheck className="icon" onClick={editCreditApp} />
            <MdClose className="icon" onClick={() => setEdit(false)} />
          </Fragment>
        ) : (
          <MdEdit className="icon" onClick={() => setEdit(true)} />
        )}
        {loading ? (
          <LoadingSpinner classes="w-6 h-6 text-white" />
        ) : (
          <TbFileTypePdf className="icon" onClick={getPdf} />
        )}
        {show ? (
          <TbChevronUp className="icon" onClick={() => setShow(false)} />
        ) : (
          <TbChevronDown className="icon" onClick={() => setShow(true)} />
        )}
      </Heading>
      {show && (
        <div className="p-4 info">
          <DetailPair
            label="Driver's License Number"
            value={partner.driversLicenseNumber}
            loading={loading}
            edit={edit}
            inputOptions={{
              id: "driversLicenseNumber",
            }}
          />
          <DetailPair
            label="Social Security Number"
            value={partner.ssn}
            loading={loading}
            edit={edit}
            inputOptions={{
              id: "ssn",
            }}
          />

          <div className="lg:col-span-2">
            <DetailPair
              label="Current Address"
              value={partner.address}
              loading={loading}
              edit={edit}
              inputOptions={{
                id: "address",
              }}
            />
          </div>

          <SelectPair
            label="Residence Type"
            value={partner.residenceType}
            options={["Own", "Rent", "Other"]}
            loading={loading}
            edit={edit}
            inputOptions={{
              id: "residenceType",
            }}
          />
          <DetailPair
            label="Monthly Rent/Mortgage"
            value={partner.monthlyRent}
            loading={loading}
            edit={edit}
            inputOptions={{
              id: "monthlyRent",
              type: "number",
              step: "0.01",
            }}
          />
          <DetailPair
            label="Years at Residence"
            value={partner.yearsAtResidence}
            loading={loading}
            edit={edit}
            inputOptions={{
              id: "yearsAtResidence",
              type: "number",
            }}
          />
          <DetailPair
            label="Months at Residence"
            value={partner.monthsAtResidence}
            type="number"
            loading={loading}
            edit={edit}
            inputOptions={{
              id: "monthsAtResidence",
              type: "number",
            }}
          />
          <DetailPair
            label="Employer"
            value={partner.employer}
            loading={loading}
            edit={edit}
            inputOptions={{
              id: "employer",
            }}
          />
          <DetailPair
            label="Occupation"
            value={partner.occupation}
            loading={loading}
            edit={edit}
            inputOptions={{
              id: "occupation",
            }}
          />
          <DetailPair
            label="Years at Current Employment"
            value={partner.employmentYears}
            loading={loading}
            edit={edit}
            inputOptions={{
              id: "employmentYears",
              type: "number",
            }}
          />
          <DetailPair
            label="Monthly Salary"
            value={partner.monthlySalary}
            loading={loading}
            edit={edit}
            inputOptions={{
              id: "monthlySalary",
              type: "number",
              step: "0.01",
            }}
          />
          <DetailPair
            label="Other Income"
            value={partner.otherIncome}
            loading={loading}
            edit={edit}
            inputOptions={{
              id: "otherIncome",
              type: "number",
              step: "0.01",
            }}
          />
          <LinkPair
            label="Picture of SSN"
            link={partner.ssnFront}
            loading={loading}
            edit={edit}
            inputOptions={{
              id: "ssnFront",
              type: "file",
            }}
          />
          <LinkPair
            label="Proof of Residence"
            link={partner.proofOfResidence}
            edit={edit}
            loading={loading}
            inputOptions={{
              id: "proofOfResidence",
              type: "file",
              multiple: true,
            }}
          />
          <LinkPair
            label="Pay Stub (YTD)"
            link={partner.payStubs}
            edit={edit}
            loading={loading}
            inputOptions={{
              id: "payStubs",
              type: "file",
              multiple: true,
            }}
          />
          <LinkPair
            label="Bank Statements (last 3)"
            link={partner.bankStatements}
            edit={edit}
            loading={loading}
            inputOptions={{
              id: "bankStatements",
              type: "file",
              multiple: true,
            }}
          />
          <LinkPair
            label="Tax Returns"
            link={partner.taxReturns}
            edit={edit}
            loading={loading}
            inputOptions={{
              id: "taxReturns",
              type: "file",
              multiple: true,
            }}
          />
          <LinkPair
            label="Selfie with ID"
            link={partner.selfieId}
            edit={edit}
            loading={loading}
            inputOptions={{
              id: "selfieId",
              type: "file",
            }}
          />
          <LinkPair
            label="Credit App Agreement"
            link={partner.creditAppAgreement}
            edit={edit}
            loading={loading}
            inputOptions={{
              id: "creditAppAgreement",
              type: "file",
            }}
          />
        </div>
      )}
    </div>
  );
}
